/**
 * Fundo: atlas do território profissional (mesa do cartógrafo).
 * Lupa sob o ponteiro; rota/nós respondem ao focus/digitação do search.
 * Sem arte licenciada — canvas procedural.
 */
import {
  Scene,
  OrthographicCamera,
  WebGLRenderer,
  Mesh,
  PlaneGeometry,
  ShaderMaterial,
  CanvasTexture,
  Clock,
  Vector2,
  Color,
  SRGBColorSpace,
  LinearFilter,
} from 'three';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const isCoarsePointer = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(pointer: coarse)').matches;

/** Nós do atlas — UV 0–1 (origem canto inferior-esquerdo no shader). */
const ATLAS_NODES = [
  { x: 0.22, y: 0.72, label: 'Lakehouse', sub: 'Custo' },
  { x: 0.48, y: 0.78, label: 'Analytics', sub: 'Modelo' },
  { x: 0.3, y: 0.52, label: 'Plataforma', sub: 'Pipelines' },
  { x: 0.72, y: 0.58, label: 'Liderança', sub: 'Time' },
  { x: 0.55, y: 0.32, label: 'Entrega', sub: 'Evidências' },
];

/** Rota incompleta: liga 4 nós e para antes do último. */
const ROUTE_POINTS = [
  [0.22, 0.72],
  [0.3, 0.52],
  [0.48, 0.78],
  [0.72, 0.58],
  [0.62, 0.42], // gap — não chega em Entrega
];

const VERT = /* glsl */ `
varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAG = /* glsl */ `
uniform sampler2D uMap;
uniform sampler2D uAtlas;
uniform float uTime;
uniform vec2 uMouse;
uniform float uMouseStrength;
uniform float uVignette;
uniform vec3 uTint;
uniform float uExplore;
uniform float uRoute;
uniform float uFocus;
uniform float uLoupeRadius;
uniform float uLoupeZoom;

varying vec2 vUv;

void main() {
  vec2 toMouse = vUv - uMouse;
  float dist = length(toMouse);

  // Lupa: zoom radial sob o ponteiro (sem tremble de tecido)
  float radius = uLoupeRadius;
  float loupeMask =
    smoothstep(radius, radius * 0.55, dist) * clamp(uMouseStrength, 0.0, 1.0);
  vec2 zoomedUv = uMouse + toMouse / max(uLoupeZoom, 1.01);
  vec2 uv = mix(vUv, zoomedUv, loupeMask);
  uv = clamp(uv, 0.001, 0.999);

  vec4 tex = texture2D(uMap, uv);
  vec4 atlas = texture2D(uAtlas, uv);

  vec3 col = tex.rgb;

  // R = progresso ao longo da rota (0→1); G = nós; B = arestas secundárias
  float routeInk = atlas.r;
  float routeVis =
    smoothstep(0.0, 0.035, routeInk) *
    (1.0 - smoothstep(uRoute, uRoute + 0.07, routeInk));
  routeVis *= mix(0.4, 1.0, uExplore);

  float nodeGlow = atlas.g * mix(0.22, 1.0, uExplore);
  float pulse = 0.85 + 0.15 * sin(uTime * 2.4 + atlas.g * 12.0);
  nodeGlow *= mix(1.0, pulse, uFocus);

  float edgeGlow = atlas.b * mix(0.12, 0.75, uExplore);

  col += uTint * (routeVis * 0.62 + nodeGlow * 0.78 + edgeGlow * 0.4);
  col += vec3(0.08, 0.06, 0.03) * uExplore * 0.12;

  // aro da lupa + leve lift de leitura no centro
  float rim =
    smoothstep(radius * 1.08, radius, dist) *
    smoothstep(radius * 0.82, radius * 0.96, dist) *
    clamp(uMouseStrength, 0.0, 1.0);
  col += uTint * rim * 0.55;
  col += vec3(0.06, 0.05, 0.03) * loupeMask * 0.22;

  float edge = smoothstep(0.0, 0.14, vUv.x) * smoothstep(0.0, 0.14, vUv.y)
    * smoothstep(0.0, 0.14, 1.0 - vUv.x) * smoothstep(0.0, 0.14, 1.0 - vUv.y);
  col *= mix(0.62, 1.0, edge);

  float vig = 1.0 - distance(vUv, vec2(0.5)) * uVignette;
  col *= clamp(vig, 0.42, 1.0);

  float grit = fract(sin(dot(vUv * 420.0 + uTime * 0.02, vec2(12.9898, 78.233))) * 43758.5453);
  col += (grit - 0.5) * 0.014;

  gl_FragColor = vec4(col, 1.0);
}
`;

/** @param {CanvasRenderingContext2D} ctx @param {number} size @param {number[][]} pts */
function strokePath(ctx, size, pts, { closed = false } = {}) {
  if (!pts.length) return;
  ctx.beginPath();
  ctx.moveTo(pts[0][0] * size, pts[0][1] * size);
  for (let i = 1; i < pts.length; i++) {
    ctx.lineTo(pts[i][0] * size, pts[i][1] * size);
  }
  if (closed) ctx.closePath();
  ctx.stroke();
}

/**
 * Rasteriza a rota no canal R com valor = progresso 0–1 ao longo do caminho.
 * Y do canvas: 0 no topo — converter de UV cartográfico (y sobe) para canvas.
 * @param {CanvasRenderingContext2D} ctx
 * @param {number} size
 * @param {number[][]} pts UV com y crescendo para cima
 * @param {number} lineWidth
 */
function paintRouteProgress(ctx, size, pts, lineWidth) {
  if (pts.length < 2) return;
  const canvasPts = pts.map(([x, y]) => [x * size, (1 - y) * size]);
  let total = 0;
  const segLens = [];
  for (let i = 1; i < canvasPts.length; i++) {
    const dx = canvasPts[i][0] - canvasPts[i - 1][0];
    const dy = canvasPts[i][1] - canvasPts[i - 1][1];
    const len = Math.hypot(dx, dy);
    segLens.push(len);
    total += len;
  }
  if (total < 1) return;

  const samples = Math.max(120, Math.floor(total / 2));
  let walked = 0;
  let seg = 0;
  let segPos = 0;

  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (let s = 0; s <= samples; s++) {
    const t = s / samples;
    const target = t * total;
    while (seg < segLens.length - 1 && walked + segLens[seg] < target) {
      walked += segLens[seg];
      seg += 1;
      segPos = 0;
    }
    const sl = segLens[seg] || 1;
    const localT = Math.min(1, (target - walked) / sl);
    const a = canvasPts[seg];
    const b = canvasPts[seg + 1];
    const x = a[0] + (b[0] - a[0]) * localT;
    const y = a[1] + (b[1] - a[1]) * localT;
    const v = Math.max(0.02, t);
    ctx.fillStyle = `rgb(${Math.round(v * 255)},0,0)`;
    ctx.beginPath();
    ctx.arc(x, y, lineWidth * 0.55, 0, Math.PI * 2);
    ctx.fill();
  }
}

/**
 * @param {number} size
 * @returns {{ map: CanvasTexture, atlas: CanvasTexture }}
 */
function createAtlasTextures(size = 1024) {
  const mapCanvas = document.createElement('canvas');
  mapCanvas.width = size;
  mapCanvas.height = size;
  const ctx = mapCanvas.getContext('2d');
  if (!ctx) throw new Error('[map-cloth] 2d context unavailable');

  const atlasCanvas = document.createElement('canvas');
  atlasCanvas.width = size;
  atlasCanvas.height = size;
  const ax = atlasCanvas.getContext('2d');
  if (!ax) throw new Error('[map-cloth] atlas 2d context unavailable');

  // --- base parchment ---
  const base = ctx.createRadialGradient(
    size * 0.48,
    size * 0.42,
    size * 0.08,
    size * 0.5,
    size * 0.5,
    size * 0.75,
  );
  base.addColorStop(0, '#1a1612');
  base.addColorStop(0.45, '#12100d');
  base.addColorStop(1, '#070809');
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);

  const img = ctx.getImageData(0, 0, size, size);
  const d = img.data;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = (y * size + x) * 4;
      const fiber = ((x * 17 + y * 13) % 7) - 3;
      const weave = (y % 4 === 0 ? -5 : 0) + (x % 5 === 0 ? -3 : 0);
      d[i] = Math.max(0, Math.min(255, d[i] + fiber + weave));
      d[i + 1] = Math.max(0, Math.min(255, d[i + 1] + fiber * 0.7 + weave));
      d[i + 2] = Math.max(0, Math.min(255, d[i + 2] + fiber * 0.45 + weave * 0.8));
    }
  }
  ctx.putImageData(img, 0, 0);

  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  // costa abstrata (território, não geografia fantástica)
  const coast = [
    [0.1, 0.18],
    [0.16, 0.28],
    [0.12, 0.4],
    [0.18, 0.52],
    [0.14, 0.64],
    [0.22, 0.74],
    [0.34, 0.82],
    [0.48, 0.86],
    [0.62, 0.84],
    [0.76, 0.88],
    [0.88, 0.8],
    [0.92, 0.66],
    [0.9, 0.5],
    [0.94, 0.36],
    [0.88, 0.22],
    [0.74, 0.14],
    [0.58, 0.1],
    [0.42, 0.12],
    [0.28, 0.1],
    [0.16, 0.14],
  ];
  ctx.strokeStyle = 'rgba(196, 163, 90, 0.55)';
  ctx.lineWidth = Math.max(1.6, size * 0.002);
  strokePath(
    ctx,
    size,
    coast.map(([x, y]) => [x, 1 - y]),
    { closed: true },
  );

  ctx.strokeStyle = 'rgba(196, 163, 90, 0.18)';
  ctx.lineWidth = Math.max(0.9, size * 0.0011);
  strokePath(
    ctx,
    size,
    coast.map(([x, y]) => [x - 0.014, 1 - (y + 0.01)]),
  );

  // regiões do portfólio
  const regions = [
    {
      pts: [
        [0.12, 0.62],
        [0.32, 0.68],
        [0.36, 0.82],
        [0.14, 0.86],
      ],
      label: 'Lakehouse / GCP',
      lx: 0.08,
      ly: 0.9,
    },
    {
      pts: [
        [0.38, 0.7],
        [0.62, 0.72],
        [0.64, 0.88],
        [0.4, 0.9],
      ],
      label: 'Analytics Eng.',
      lx: 0.42,
      ly: 0.94,
    },
    {
      pts: [
        [0.14, 0.38],
        [0.4, 0.42],
        [0.42, 0.58],
        [0.16, 0.56],
      ],
      label: 'Plataforma',
      lx: 0.08,
      ly: 0.48,
    },
    {
      pts: [
        [0.58, 0.42],
        [0.86, 0.4],
        [0.88, 0.62],
        [0.6, 0.66],
      ],
      label: 'Liderança',
      lx: 0.9,
      ly: 0.52,
    },
    {
      pts: [
        [0.4, 0.16],
        [0.7, 0.14],
        [0.72, 0.34],
        [0.42, 0.36],
      ],
      label: 'Entrega',
      lx: 0.78,
      ly: 0.12,
    },
  ];

  for (const region of regions) {
    ctx.strokeStyle = 'rgba(196, 163, 90, 0.5)';
    ctx.lineWidth = Math.max(1.2, size * 0.0015);
    strokePath(
      ctx,
      size,
      region.pts.map(([x, y]) => [x, 1 - y]),
      { closed: true },
    );
    ctx.fillStyle = 'rgba(196, 163, 90, 0.04)';
    ctx.beginPath();
    const r0 = region.pts[0];
    ctx.moveTo(r0[0] * size, (1 - r0[1]) * size);
    for (let i = 1; i < region.pts.length; i++) {
      ctx.lineTo(region.pts[i][0] * size, (1 - region.pts[i][1]) * size);
    }
    ctx.closePath();
    ctx.fill();

    ctx.fillStyle = 'rgba(196, 163, 90, 0.38)';
    ctx.font = `${Math.max(9, size * 0.014)}px "Roboto Mono", ui-monospace, monospace`;
    ctx.textAlign = region.lx > 0.7 ? 'right' : 'left';
    ctx.fillText(region.label, region.lx * size, (1 - region.ly) * size);
  }

  // grade cartográfica
  ctx.strokeStyle = 'rgba(196, 163, 90, 0.08)';
  ctx.lineWidth = 0.75;
  const step = size / 14;
  for (let i = 1; i < 14; i++) {
    ctx.beginPath();
    ctx.moveTo(i * step, size * 0.06);
    ctx.lineTo(i * step, size * 0.94);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(size * 0.06, i * step);
    ctx.lineTo(size * 0.94, i * step);
    ctx.stroke();
  }

  // rota estática tracejada (idle — incompleta)
  ctx.strokeStyle = 'rgba(196, 163, 90, 0.42)';
  ctx.lineWidth = Math.max(1.3, size * 0.0016);
  ctx.setLineDash([size * 0.012, size * 0.01]);
  strokePath(
    ctx,
    size,
    ROUTE_POINTS.map(([x, y]) => [x, 1 - y]),
  );
  ctx.setLineDash([]);

  // gap marker (destino ainda não alcançado)
  const gap = ROUTE_POINTS[ROUTE_POINTS.length - 1];
  ctx.strokeStyle = 'rgba(196, 163, 90, 0.55)';
  ctx.lineWidth = Math.max(1.2, size * 0.0014);
  const gx = gap[0] * size;
  const gy = (1 - gap[1]) * size;
  const tick = size * 0.012;
  ctx.beginPath();
  ctx.moveTo(gx - tick, gy - tick);
  ctx.lineTo(gx + tick, gy + tick);
  ctx.moveTo(gx + tick, gy - tick);
  ctx.lineTo(gx - tick, gy + tick);
  ctx.stroke();

  // nós idle (anéis)
  for (const node of ATLAS_NODES) {
    const nx = node.x * size;
    const ny = (1 - node.y) * size;
    const r = size * 0.012;
    ctx.strokeStyle = 'rgba(196, 163, 90, 0.48)';
    ctx.lineWidth = Math.max(1.1, size * 0.0013);
    ctx.beginPath();
    ctx.arc(nx, ny, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = 'rgba(196, 163, 90, 0.2)';
    ctx.beginPath();
    ctx.arc(nx, ny, r * 0.35, 0, Math.PI * 2);
    ctx.fill();

    ctx.fillStyle = 'rgba(196, 163, 90, 0.32)';
    ctx.font = `${Math.max(8, size * 0.011)}px "Roboto Mono", ui-monospace, monospace`;
    ctx.textAlign = 'left';
    ctx.fillText(node.sub, nx + r * 1.6, ny + size * 0.004);
  }

  // bússola
  const cx = size * 0.16;
  const cy = size * 0.86;
  const arm = size * 0.038;
  ctx.strokeStyle = 'rgba(196, 163, 90, 0.45)';
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(cx, cy - arm);
  ctx.lineTo(cx, cy + arm);
  ctx.moveTo(cx - arm, cy);
  ctx.lineTo(cx + arm, cy);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(cx, cy - arm);
  ctx.lineTo(cx + arm * 0.22, cy - arm * 0.35);
  ctx.lineTo(cx - arm * 0.22, cy - arm * 0.35);
  ctx.closePath();
  ctx.stroke();
  ctx.fillStyle = 'rgba(196, 163, 90, 0.4)';
  ctx.font = `${Math.max(8, size * 0.012)}px "Roboto Mono", ui-monospace, monospace`;
  ctx.textAlign = 'center';
  ctx.fillText('N', cx, cy - arm - size * 0.008);

  // borda queimada (mais leve que antes)
  const edge = ctx.createRadialGradient(
    size * 0.5,
    size * 0.5,
    size * 0.42,
    size * 0.5,
    size * 0.5,
    size * 0.74,
  );
  edge.addColorStop(0, 'rgba(0,0,0,0)');
  edge.addColorStop(0.78, 'rgba(0,0,0,0.08)');
  edge.addColorStop(1, 'rgba(0,0,0,0.55)');
  ctx.fillStyle = edge;
  ctx.fillRect(0, 0, size, size);

  // --- atlas layer (R route progress, G nodes, B edges) ---
  ax.clearRect(0, 0, size, size);
  ax.fillStyle = '#000';
  ax.fillRect(0, 0, size, size);

  paintRouteProgress(ax, size, ROUTE_POINTS, Math.max(4, size * 0.006));

  // arestas B entre nós adjacentes na rota
  ax.globalCompositeOperation = 'lighter';
  for (let i = 0; i < ROUTE_POINTS.length - 1; i++) {
    const a = ROUTE_POINTS[i];
    const b = ROUTE_POINTS[i + 1];
    const grad = ax.createLinearGradient(
      a[0] * size,
      (1 - a[1]) * size,
      b[0] * size,
      (1 - b[1]) * size,
    );
    grad.addColorStop(0, 'rgb(0,0,90)');
    grad.addColorStop(0.5, 'rgb(0,0,160)');
    grad.addColorStop(1, 'rgb(0,0,90)');
    ax.strokeStyle = grad;
    ax.lineWidth = Math.max(2, size * 0.003);
    ax.beginPath();
    ax.moveTo(a[0] * size, (1 - a[1]) * size);
    ax.lineTo(b[0] * size, (1 - b[1]) * size);
    ax.stroke();
  }

  for (const node of ATLAS_NODES) {
    const nx = node.x * size;
    const ny = (1 - node.y) * size;
    const glow = ax.createRadialGradient(nx, ny, 0, nx, ny, size * 0.045);
    glow.addColorStop(0, 'rgb(0,255,40)');
    glow.addColorStop(0.35, 'rgb(0,160,10)');
    glow.addColorStop(1, 'rgb(0,0,0)');
    ax.fillStyle = glow;
    ax.beginPath();
    ax.arc(nx, ny, size * 0.045, 0, Math.PI * 2);
    ax.fill();
  }
  ax.globalCompositeOperation = 'source-over';

  const mapTex = new CanvasTexture(mapCanvas);
  mapTex.colorSpace = SRGBColorSpace;
  mapTex.magFilter = LinearFilter;
  mapTex.minFilter = LinearFilter;
  mapTex.needsUpdate = true;

  const atlasTex = new CanvasTexture(atlasCanvas);
  atlasTex.colorSpace = SRGBColorSpace;
  atlasTex.magFilter = LinearFilter;
  atlasTex.minFilter = LinearFilter;
  atlasTex.needsUpdate = true;

  return { map: mapTex, atlas: atlasTex };
}

export class MapClothBackground {
  /**
   * @param {HTMLElement} host
   */
  constructor(host) {
    this.host = host;
    this._reduced = prefersReducedMotion();
    this._paused = false;
    this._externalPause = false;
    this._hidden = false;
    this._raf = 0;
    this._started = false;
    this._mouse = new Vector2(0.5, 0.5);
    this._mouseSmooth = new Vector2(0.5, 0.5);
    this._strength = 0;
    this._strengthTarget = 0;
    this._focusTarget = 0;
    this._exploreTarget = 0.12;
    this._routeTarget = 0.28;
    this._focus = 0;
    this._explore = 0.12;
    this._route = 0.28;
    this._halfW = 1;
    this._halfH = 1;
    this._cover = 2.08;
    const coarse = isCoarsePointer();
    this._loupeSearch = {
      radius: coarse ? 0.16 : 0.12,
      zoom: coarse ? 1.55 : 1.85,
    };
    this._loupeBoard = {
      radius: coarse ? 0.07 : 0.055,
      zoom: coarse ? 1.28 : 1.35,
    };
    this._loupeRadius = this._loupeSearch.radius;
    this._loupeZoom = this._loupeSearch.zoom;
    this._loupeRadiusTarget = this._loupeSearch.radius;
    this._loupeZoomTarget = this._loupeSearch.zoom;
    this.clock = new Clock(false);

    if (this._reduced) {
      host.classList.add('is-static');
      return;
    }

    this._init();
  }

  _init() {
    const canvas = document.createElement('canvas');
    canvas.className = 'intent-atmosphere__canvas';
    canvas.setAttribute('aria-hidden', 'true');
    this.host.appendChild(canvas);
    this.canvas = canvas;

    let renderer;
    try {
      renderer = new WebGLRenderer({
        canvas,
        antialias: false,
        alpha: false,
        powerPreference: 'low-power',
        failIfMajorPerformanceCaveat: false,
      });
      if (!renderer.getContext()) throw new Error('no WebGL context');
    } catch (err) {
      console.warn('[map-cloth] WebGL unavailable', err);
      this.host.classList.add('is-static');
      canvas.remove();
      return;
    }

    this.renderer = renderer;
    const dprCap = isCoarsePointer() ? 1.15 : 1.35;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, dprCap));
    renderer.setClearColor(new Color('#070809'), 1);
    renderer.autoClear = true;

    this.scene = new Scene();
    this.camera = new OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    this.camera.position.z = 2;

    const segments = 1;
    const geo = new PlaneGeometry(2.15, 2.15, segments, segments);
    const textures = createAtlasTextures(isCoarsePointer() ? 768 : 1024);
    this.texture = textures.map;
    this.atlasTexture = textures.atlas;

    this.material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uMouse: { value: new Vector2(0.5, 0.5) },
        uMouseStrength: { value: 0 },
        uMap: { value: this.texture },
        uAtlas: { value: this.atlasTexture },
        uVignette: { value: 0.82 },
        uTint: { value: new Color('#c4a35a') },
        uExplore: { value: this._explore },
        uRoute: { value: this._route },
        uFocus: { value: this._focus },
        uLoupeRadius: { value: this._loupeRadius },
        uLoupeZoom: { value: this._loupeZoom },
      },
      vertexShader: VERT,
      fragmentShader: FRAG,
      depthTest: false,
      depthWrite: false,
    });

    this.mesh = new Mesh(geo, this.material);
    this.scene.add(this.mesh);

    this.host.classList.add('has-webgl');
    this._onResize = () => this._resize();
    this._onPointer = (e) => this._onPointerMove(e);
    this._onPointerLeave = () => {
      this._strengthTarget = 0;
    };
    this._onVisibility = () => {
      this._hidden = document.visibilityState === 'hidden';
      this._syncLoop();
    };

    window.addEventListener('resize', this._onResize, { passive: true });
    window.addEventListener('pointermove', this._onPointer, { passive: true });
    document.addEventListener('visibilitychange', this._onVisibility);
    window.addEventListener('blur', this._onPointerLeave);

    this._resize();
    this._started = true;
    this.clock.start();
    this._syncLoop();
  }

  /**
   * Energia do atlas: focus do search + digitação.
   * @param {{ focus?: boolean, typing?: boolean, queryLen?: number }} state
   */
  setExploreState(state = {}) {
    const focus = Boolean(state.focus);
    const typing = Boolean(state.typing);
    const queryLen = Math.max(0, Number(state.queryLen) || 0);
    const lenNorm = Math.min(1, queryLen / 48);

    this._focusTarget = focus ? 1 : 0;
    // idle baixo → focus sobe → digitação empurra rota
    this._exploreTarget = focus ? 0.55 + lenNorm * 0.4 + (typing ? 0.08 : 0) : 0.12;
    this._routeTarget = focus ? 0.42 + lenNorm * 0.5 : 0.28;
    this._exploreTarget = Math.min(1, this._exploreTarget);
    this._routeTarget = Math.min(0.96, this._routeTarget);

    this.host?.classList.toggle('is-exploring', focus || queryLen > 0);

    if (this._reduced || !this.material) return;
  }

  /**
   * Após Projetar a lupa encolhe; em Nova busca volta ao tamanho cheio.
   * @param {'search' | 'board'} mode
   */
  setLoupeMode(mode = 'search') {
    const pack = mode === 'board' ? this._loupeBoard : this._loupeSearch;
    this._loupeRadiusTarget = pack.radius;
    this._loupeZoomTarget = pack.zoom;
    if (this._reduced || !this.material) return;
  }

  pause() {
    this._externalPause = true;
    this._syncLoop();
  }

  resume() {
    this._externalPause = false;
    this._syncLoop();
  }

  dispose() {
    this._paused = true;
    if (this._raf) {
      cancelAnimationFrame(this._raf);
      this._raf = 0;
    }
    if (this._onResize) window.removeEventListener('resize', this._onResize);
    if (this._onPointer) window.removeEventListener('pointermove', this._onPointer);
    if (this._onPointerLeave) window.removeEventListener('blur', this._onPointerLeave);
    if (this._onVisibility) {
      document.removeEventListener('visibilitychange', this._onVisibility);
    }

    this.material?.dispose();
    this.mesh?.geometry?.dispose();
    this.texture?.dispose();
    this.atlasTexture?.dispose();
    this.renderer?.dispose();
    this.canvas?.remove();
  }

  _syncLoop() {
    if (!this._started) return;
    const shouldRun = !this._externalPause && !this._hidden;
    if (shouldRun && !this._raf) {
      this._paused = false;
      this.clock.getDelta();
      this._raf = requestAnimationFrame(this._tick);
    } else if (!shouldRun && this._raf) {
      this._paused = true;
      cancelAnimationFrame(this._raf);
      this._raf = 0;
    }
  }

  _onPointerMove(e) {
    const w = window.innerWidth || 1;
    const h = window.innerHeight || 1;
    const ndcX = (e.clientX / w) * 2 - 1;
    const ndcY = 1 - (e.clientY / h) * 2;
    const worldX = ndcX * this._halfW;
    const worldY = ndcY * this._halfH;
    const cover = Math.max(this._cover, 0.001);
    this._mouse.set(worldX / cover + 0.5, worldY / cover + 0.5);
    this._strengthTarget = 1;
  }

  _resize() {
    if (!this.renderer || !this.mesh) return;
    const w = this.host.clientWidth || window.innerWidth;
    const h = this.host.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false);

    const aspect = w / Math.max(h, 1);
    const halfW = aspect >= 1 ? aspect : 1;
    const halfH = aspect >= 1 ? 1 : 1 / aspect;
    this._halfW = halfW;
    this._halfH = halfH;
    this.camera.left = -halfW;
    this.camera.right = halfW;
    this.camera.top = halfH;
    this.camera.bottom = -halfH;
    this.camera.updateProjectionMatrix();

    const cover = Math.max(halfW, halfH) * 2.08;
    this._cover = cover;
    this.mesh.scale.set(cover / 2.15, cover / 2.15, 1);
  }

  _tick = () => {
    if (this._paused) {
      this._raf = 0;
      return;
    }
    this._raf = requestAnimationFrame(this._tick);

    const dt = Math.min(this.clock.getDelta(), 0.05);
    const t = this.clock.elapsedTime;

    this._mouseSmooth.lerp(this._mouse, 1 - Math.exp(-dt * 7));
    this._strength += (this._strengthTarget - this._strength) * (1 - Math.exp(-dt * 4.5));
    this._strengthTarget *= Math.exp(-dt * 0.55);

    // ease ~500ms para explore/focus/route/loupe
    const ease = 1 - Math.exp(-dt * 5.2);
    this._focus += (this._focusTarget - this._focus) * ease;
    this._explore += (this._exploreTarget - this._explore) * ease;
    this._route += (this._routeTarget - this._route) * ease;
    this._loupeRadius += (this._loupeRadiusTarget - this._loupeRadius) * ease;
    this._loupeZoom += (this._loupeZoomTarget - this._loupeZoom) * ease;

    const u = this.material.uniforms;
    u.uTime.value = t;
    u.uMouse.value.copy(this._mouseSmooth);
    u.uMouseStrength.value = this._strength;
    u.uFocus.value = this._focus;
    u.uExplore.value = this._explore;
    u.uRoute.value = this._route;
    u.uLoupeRadius.value = this._loupeRadius;
    u.uLoupeZoom.value = this._loupeZoom;

    this.renderer.render(this.scene, this.camera);
  };
}
